import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { Box, Divider, Paper, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { NimbuxHotelCheckOption } from '@bandido/shared/interfaces';
import { InfoLanguage, HotelCheckOptionType } from '@bandido/shared/enums';

import { useGetHotelNimbuxHotelById } from '@frontend/features/hotel-content-manager/queries';
import { useGenerateTranslations, usePatchHotel } from '@frontend/features/hotel-content-manager/mutations';

import { CheckTimeSection } from '@frontend/features/hotel-content-manager/components/NimbuxCheckOptions/CheckTimeSection';
import { HotelCheckinCheckoutLoader } from '@frontend/features/hotel-content-manager/components/NimbuxCheckOptions/HotelCheckinCheckoutLoader';
import { getDefaultValues } from '@frontend/features/hotel-content-manager/components/NimbuxCheckOptions/utils';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';


const timeOptions = (() => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      times.push(timeString);
    }
  }
  return times;
})();

const ageOptions = (() => {
  const ages = [];
  for (let age = 18; age <= 85; age++) {
    ages.push(age.toString());
  }
  return ages;
})();

export const HotelContentCheckinCheckout = () => {
  const { t } = useTranslation();
  const { hotelId } = useParams();
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();
  const { data: nimbuxHotel, isLoading: isLoadingNimbuxHotel } = useGetHotelNimbuxHotelById(Number(hotelId));
  const [checkin, setCheckin] = useState<NimbuxHotelCheckOption>(getDefaultValues(HotelCheckOptionType.CHECK_IN));
  const [checkout, setCheckout] = useState<NimbuxHotelCheckOption>(getDefaultValues(HotelCheckOptionType.CHECK_OUT));
  const [isGeneratingTranslations, setIsGeneratingTranslations] = useState(false);

  const { mutate: patchHotel, isLoading: isUpdating } = usePatchHotel({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getHotelNimbuxHotelById, Number(hotelId)]);
      addToast({
        color: 'success',
        message: t('hotelContent.view.checkinCheckout.messages.updateSuccess'),
      });
    },
    onError: () => {
      addToast({
        color: 'error',
        message: t('hotelContent.view.checkinCheckout.messages.updateError'),
      });
    }
  });

  const { mutateAsync: generateTranslations } = useGenerateTranslations({
    onSuccess: () => {
      addToast({
        color: 'success',
        message: t('hotelContent.view.checkinCheckout.messages.generateTranslationsSuccess'),
      });
    }
  });

  const updateCheckOption = (type: HotelCheckOptionType, updatedOption: NimbuxHotelCheckOption) => {
    if (type === HotelCheckOptionType.CHECK_IN) {
      setCheckin(updatedOption);
    } else {
      setCheckout(updatedOption);
    }
  };

  const handleTimeUpdate = (type: HotelCheckOptionType) => (field: 'from' | 'to', value: string) => {
    const currentData = type === HotelCheckOptionType.CHECK_IN ? checkin : checkout;
    
    if (!currentData) return;
    
    const updatedData = {
      ...currentData,
      [field === 'from' ? 'startTime' : 'endTime']: value
    };
    
    updateCheckOption(type, updatedData);
  };

  const handleAgeUpdate = (type: HotelCheckOptionType) => (value: string) => {
    const currentData = type === HotelCheckOptionType.CHECK_IN ? checkin : checkout;
    
    if (!currentData) return;
    
    const updatedData = {
      ...currentData,
      minAge: parseInt(value) || undefined
    };
  
    updateCheckOption(type, updatedData);
  };

  const handleInstructionsUpdate = (type: HotelCheckOptionType) => (language: InfoLanguage, data: string) => {
    const currentData = type === HotelCheckOptionType.CHECK_IN ? checkin : checkout;
    
    if (!currentData) return;
    
    const updatedData = {
      ...currentData,
      instructions: {
        ...currentData.instructions,
        [language]: data
      }
    };
    
    updateCheckOption(type, updatedData);
  };

  const handleGenerateWithAI = (type: HotelCheckOptionType) => async () => {
    setIsGeneratingTranslations(true);
    const currentData = type === HotelCheckOptionType.CHECK_IN ? checkin : checkout;
    const { data: { instructions } } = await generateTranslations({ text: currentData.instructions[InfoLanguage.Spanish] || '' });

    if (!currentData) return;
    
    updateCheckOption(type, {
      ...currentData,
      instructions
    });

    setIsGeneratingTranslations(false);
  };

  const handleSave = () => {
    patchHotel({
      id: Number(hotelId),
      checkOptions: [checkin, checkout]
    });
  }

  useEffect(() => {
    if (nimbuxHotel) {
      setCheckin(nimbuxHotel.checkOptions.find(option => option.type === HotelCheckOptionType.CHECK_IN) || getDefaultValues(HotelCheckOptionType.CHECK_IN));
      setCheckout(nimbuxHotel.checkOptions.find(option => option.type === HotelCheckOptionType.CHECK_OUT) || getDefaultValues(HotelCheckOptionType.CHECK_OUT));
    }
  }, [nimbuxHotel]);

  return (
    <Paper elevation={0} sx={{maxWidth:1082}}>
      {isLoadingNimbuxHotel ? (
        <HotelCheckinCheckoutLoader />
      ) : (
        <Box
          borderRadius={2}
          p={2}
          display="flex"
          flexDirection="column"
          gap={5}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('hotelContent.view.checkinCheckout.title')}</Typography>
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSave}
              loading={isUpdating}
            >
              {t('common.form.save')}
            </LoadingButton>
          </Box>

          <CheckTimeSection
            type="checkin"
            title={t('hotelContent.view.checkinCheckout.checkIn')}
            ageLabel={t('hotelContent.view.checkinCheckout.minimumAgeForCheckin')}
            instructionsLabel={t('hotelContent.view.checkinCheckout.instructionsForCheckin')}
            instructionsPlaceholder={t('hotelContent.view.checkinCheckout.instructionsForCheckin')}
            data={checkin!}
            onUpdateTime={handleTimeUpdate(HotelCheckOptionType.CHECK_IN)}
            onUpdateAge={handleAgeUpdate(HotelCheckOptionType.CHECK_IN)}
            onUpdateInstructions={handleInstructionsUpdate(HotelCheckOptionType.CHECK_IN)}
            onGenerateWithAI={handleGenerateWithAI(HotelCheckOptionType.CHECK_IN)}
            isGeneratingTranslations={isGeneratingTranslations}
            timeOptions={timeOptions}
            ageOptions={ageOptions}
          />

          <Divider />

          <CheckTimeSection
            type="checkout"
            title={t('hotelContent.view.checkinCheckout.checkOut')}
            ageLabel={t('hotelContent.view.checkinCheckout.minimumAgeForCheckout')}
            instructionsLabel={t('hotelContent.view.checkinCheckout.instructionsForCheckout')}
            instructionsPlaceholder={t('hotelContent.view.checkinCheckout.instructionsForCheckout')}
            data={checkout!}
            onUpdateTime={handleTimeUpdate(HotelCheckOptionType.CHECK_OUT)}
            onUpdateAge={handleAgeUpdate(HotelCheckOptionType.CHECK_OUT)}
            onUpdateInstructions={handleInstructionsUpdate(HotelCheckOptionType.CHECK_OUT)}
            onGenerateWithAI={handleGenerateWithAI(HotelCheckOptionType.CHECK_OUT)}
            isGeneratingTranslations={isGeneratingTranslations}
            timeOptions={timeOptions}
            ageOptions={ageOptions}
          />
        </Box>
      )}
    </Paper>
  );
};
